import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useWorkspace, useUpdateWorkspace } from "../../hooks/useWorkspace";
import {
  useNotificationPrefs,
  useUpdateNotificationPrefs,
  type NotificationDigestCadence,
  type PerEventToggles,
} from "../../hooks/useNotificationPrefs";
import {
  useTelegramStatus,
  useTelegramBind,
  useTeamsWebhook,
  useUpdateTeamsWebhook,
} from "../../hooks/useChannelSettings";

function SettingsInner() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const ws = useWorkspace(workspaceId);
  const update = useUpdateWorkspace(workspaceId);

  const [name, setName] = useState("");
  const [cxoRoleTag, setCxoRoleTag] = useState("");

  useEffect(() => {
    if (ws.data?.workspace) {
      setName(ws.data.workspace.name);
      setCxoRoleTag(ws.data.workspace.cxoRoleTag);
    }
  }, [ws.data?.workspace]);

  const isCxo = ws.data?.currentUserRole === "cxo";
  const dirty =
    !!ws.data?.workspace &&
    (name !== ws.data.workspace.name ||
      cxoRoleTag !== ws.data.workspace.cxoRoleTag);

  async function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!dirty) return;
    const patch: { name?: string; cxoRoleTag?: string } = {};
    if (name !== ws.data?.workspace.name) patch.name = name;
    if (cxoRoleTag !== ws.data?.workspace.cxoRoleTag) {
      patch.cxoRoleTag = cxoRoleTag;
    }
    await update.mutateAsync(patch).catch(() => {
      /* surfaced via mutation state */
    });
  }

  return (
    <div className="main-inner" data-testid="settings">
      <div className="greet-row">
        <span className="greet">Settings</span>
        <span className="greet-date">WORKSPACE</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">Workspace identity</div>
        <div className="lede">
          {isCxo
            ? "Update workspace name and CXO role tag. Notification prefs and calibration view ship in V1.1."
            : "Read-only — only the CXO can edit workspace identity."}
        </div>
      </div>

      <form
        onSubmit={handleSave}
        data-testid="settings-form"
        style={{
          marginTop: 18,
          padding: 20,
          border: "1px solid var(--border)",
          borderRadius: 8,
          background: "white",
          maxWidth: 540,
        }}
      >
        <label
          htmlFor="ws-name"
          style={{
            display: "block",
            fontSize: 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--text-3)",
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          Workspace name
        </label>
        <input
          id="ws-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!isCxo}
          required
          maxLength={120}
          data-testid="settings-name"
          style={{
            width: "100%",
            padding: "8px 10px",
            border: "1px solid var(--border)",
            borderRadius: 6,
            fontSize: 13,
            marginBottom: 16,
          }}
        />

        <label
          htmlFor="ws-tag"
          style={{
            display: "block",
            fontSize: 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--text-3)",
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          CXO role tag
        </label>
        <input
          id="ws-tag"
          type="text"
          value={cxoRoleTag}
          onChange={(e) => setCxoRoleTag(e.target.value)}
          disabled={!isCxo}
          required
          maxLength={40}
          data-testid="settings-cxo-tag"
          placeholder="e.g. cfo"
          style={{
            width: "100%",
            padding: "8px 10px",
            border: "1px solid var(--border)",
            borderRadius: 6,
            fontSize: 13,
            marginBottom: 16,
          }}
        />

        {isCxo ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="submit"
              className="btn primary"
              disabled={!dirty || update.isPending}
              data-testid="settings-save"
            >
              {update.isPending ? "Saving…" : "Save changes"}
            </button>
            {update.isError ? (
              <span
                data-testid="settings-error"
                style={{ fontSize: 12, color: "var(--warn)" }}
              >
                {update.error instanceof Error
                  ? update.error.message
                  : "Save failed"}
              </span>
            ) : null}
            {update.isSuccess && !dirty ? (
              <span
                data-testid="settings-success"
                style={{ fontSize: 12, color: "var(--accent)" }}
              >
                Saved.
              </span>
            ) : null}
          </div>
        ) : null}
      </form>

      <NotificationPreferencesSection workspaceId={workspaceId} />
      <TelegramSection />
      {isCxo && <TeamsSection workspaceId={workspaceId} />}

      <div className="section-h">
        <span className="ttl">Calibration view</span>
        <span className="meta">Read-only</span>
      </div>
      <div
        className="callout"
        data-testid="settings-calibration-stub"
        style={{ alignItems: "center" }}
      >
        <div>
          <div className="ttl">Calibration profile is managed by Plan-it</div>
          <div className="sub">
            The planner derives calibration adjustments from your historical
            outcomes. No tunables exposed in V1.
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <div className="main-inner" data-testid="settings-error">
      <div className="greet-row">
        <span className="greet">Settings unavailable</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">ERROR</div>
        <div className="lede">{error.message}</div>
      </div>
      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          className="btn primary"
          onClick={resetErrorBoundary}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

const EVENT_LABELS: Array<{ key: keyof PerEventToggles; label: string; sub: string }> = [
  {
    key: "decision_needed",
    label: "Decision needed",
    sub: "A play step is awaiting your sign-off.",
  },
  {
    key: "replan_ready",
    label: "Replan ready",
    sub: "A replan has been proposed and needs review.",
  },
  {
    key: "agent_failure",
    label: "Agent failure",
    sub: "An autonomous step failed and needs manual handling.",
  },
  {
    key: "kr_target_hit",
    label: "KR target hit",
    sub: "A linked Key Result reached or crossed its target value.",
  },
];

function NotificationPreferencesSection({ workspaceId }: { workspaceId: string }) {
  // Two scopes: global (default for the user across all workspaces) and
  // workspace-specific (overrides global for this workspace only). The
  // current UX shows workspace-specific by default since Settings lives
  // inside a workspace; users can switch to global via the toggle.
  const [scope, setScope] = useState<"workspace" | "global">("workspace");
  const effectiveWsId = scope === "workspace" ? workspaceId : null;
  const prefsQ = useNotificationPrefs(effectiveWsId);
  const updateMut = useUpdateNotificationPrefs();

  const prefs = prefsQ.data?.prefs;
  const [draftCadence, setDraftCadence] = useState<NotificationDigestCadence>("daily");
  const [draftEvents, setDraftEvents] = useState<PerEventToggles>({
    decision_needed: true,
    replan_ready: true,
    agent_failure: true,
    kr_target_hit: false,
  });

  // Sync draft to server response when it lands or scope changes.
  useEffect(() => {
    if (prefs) {
      setDraftCadence(prefs.digest_cadence);
      setDraftEvents(prefs.per_event);
    }
  }, [prefs?.digest_cadence, prefs?.per_event, scope]);

  const dirty =
    !!prefs &&
    (draftCadence !== prefs.digest_cadence ||
      JSON.stringify(draftEvents) !== JSON.stringify(prefs.per_event));

  function handleSave() {
    if (!dirty) return;
    updateMut.mutate({
      digest_cadence: draftCadence,
      per_event: draftEvents,
      workspace_id: effectiveWsId,
    });
  }

  return (
    <>
      <div className="section-h">
        <span className="ttl">Notification preferences</span>
        <span className="meta" data-testid="settings-notif-scope-label">
          {scope === "workspace" ? "this workspace" : "global default"}
        </span>
      </div>
      <div
        data-testid="settings-notif"
        style={{
          padding: 16,
          border: "1px solid var(--border)",
          borderRadius: 8,
          background: "white",
          fontSize: 13,
        }}
      >
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          <button
            type="button"
            data-testid="settings-notif-scope-workspace"
            className={`btn ${scope === "workspace" ? "primary" : ""}`}
            onClick={() => setScope("workspace")}
            style={{ fontSize: 12, padding: "5px 10px" }}
          >
            This workspace
          </button>
          <button
            type="button"
            data-testid="settings-notif-scope-global"
            className={`btn ${scope === "global" ? "primary" : ""}`}
            onClick={() => setScope("global")}
            style={{ fontSize: 12, padding: "5px 10px" }}
          >
            Global default
          </button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--text-2)",
              marginBottom: 6,
            }}
          >
            Digest cadence
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            {(["off", "daily", "weekly"] as NotificationDigestCadence[]).map((c) => (
              <label
                key={c}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}
              >
                <input
                  type="radio"
                  name="digest-cadence"
                  value={c}
                  data-testid={`settings-notif-cadence-${c}`}
                  checked={draftCadence === c}
                  onChange={() => setDraftCadence(c)}
                />
                <span style={{ fontSize: 12, textTransform: "capitalize" }}>
                  {c === "off" ? "Off" : c === "daily" ? "Daily" : "Weekly"}
                </span>
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>
            Off = no digests; per-event toggles below still fire as real-time
            notifications. Daily/weekly bundle the same events into one
            scheduled summary.
          </div>
        </div>

        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--text-2)",
            marginBottom: 6,
          }}
        >
          Per-event notifications
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {EVENT_LABELS.map((e) => (
            <label
              key={e.key}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                padding: "6px 0",
                borderBottom: "1px solid var(--border)",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                data-testid={`settings-notif-event-${e.key}`}
                checked={draftEvents[e.key]}
                onChange={(ev) =>
                  setDraftEvents((prev) => ({ ...prev, [e.key]: ev.target.checked }))
                }
                style={{ marginTop: 2 }}
              />
              <div>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{e.label}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)" }}>{e.sub}</div>
              </div>
            </label>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 14 }}>
          <button
            type="button"
            className="btn primary"
            data-testid="settings-notif-save"
            onClick={handleSave}
            disabled={!dirty || updateMut.isPending}
            style={{ fontSize: 12 }}
          >
            {updateMut.isPending ? "Saving…" : "Save preferences"}
          </button>
          {updateMut.isSuccess && !dirty ? (
            <span
              data-testid="settings-notif-saved"
              style={{ fontSize: 11, color: "var(--accent)" }}
            >
              Saved.
            </span>
          ) : null}
          {updateMut.isError ? (
            <span
              data-testid="settings-notif-error"
              style={{ fontSize: 11, color: "var(--warn)" }}
            >
              {updateMut.error instanceof Error
                ? updateMut.error.message
                : "Save failed"}
            </span>
          ) : null}
        </div>
      </div>
    </>
  );
}

function TelegramSection() {
  const statusQ = useTelegramStatus();
  const bind = useTelegramBind();
  const [deepLink, setDeepLink] = useState<string | null>(null);

  const configured = statusQ.data?.configured ?? false;
  const bound = statusQ.data?.bound ?? false;

  function handleBind() {
    bind.mutate(undefined, {
      onSuccess: (r) => setDeepLink(r.deepLink),
    });
  }

  return (
    <>
      <div className="section-h">
        <span className="ttl">Telegram notifications</span>
        <span className="meta">{bound ? "connected" : "not connected"}</span>
      </div>
      <div data-testid="settings-telegram" style={{ padding: 16, border: "1px solid var(--border)", borderRadius: 8, background: "white", fontSize: 13 }}>
        {!configured ? (
          <div style={{ color: "var(--text-3)", fontSize: 12 }}>
            Telegram is not configured on this server. Ask your administrator to set <code>TELEGRAM_BOT_TOKEN</code>.
          </div>
        ) : bound ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "var(--accent)", fontWeight: 600 }}>✓ Connected</span>
            <span style={{ color: "var(--text-3)", fontSize: 12 }}>Your Telegram account will receive play notifications.</span>
            <button type="button" className="btn" style={{ fontSize: 12, marginLeft: "auto" }} onClick={handleBind} disabled={bind.isPending}>
              Reconnect
            </button>
          </div>
        ) : deepLink ? (
          <div>
            <div style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 8 }}>
              Open this link in Telegram to complete the connection. The link expires in 10 minutes.
            </div>
            <a href={deepLink} target="_blank" rel="noopener noreferrer" className="btn primary" style={{ fontSize: 12, display: "inline-block" }}>
              Open in Telegram →
            </a>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>Connect your Telegram account to receive real-time play notifications.</div>
            <button type="button" className="btn primary" style={{ fontSize: 12, marginLeft: "auto", whiteSpace: "nowrap" }} onClick={handleBind} disabled={bind.isPending}>
              {bind.isPending ? "Generating…" : "Connect Telegram"}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function TeamsSection({ workspaceId }: { workspaceId: string }) {
  const webhookQ = useTeamsWebhook(workspaceId);
  const updateMut = useUpdateTeamsWebhook(workspaceId);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (webhookQ.data?.webhook_url) setDraft(webhookQ.data.webhook_url);
  }, [webhookQ.data?.webhook_url]);

  const saved = webhookQ.data?.webhook_url ?? null;
  const dirty = draft.trim() !== (saved ?? "");

  return (
    <>
      <div className="section-h">
        <span className="ttl">Microsoft Teams notifications</span>
        <span className="meta">{saved ? "configured" : "not configured"}</span>
      </div>
      <div data-testid="settings-teams" style={{ padding: 16, border: "1px solid var(--border)", borderRadius: 8, background: "white", fontSize: 13 }}>
        <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 8 }}>
          Paste an <strong>Incoming Webhook</strong> URL from a Teams channel to send play notifications there.
        </div>
        <input
          type="url"
          placeholder="https://outlook.office.com/webhook/..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-soft)", color: "var(--text)", fontSize: 13, boxSizing: "border-box", marginBottom: 10 }}
        />
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            type="button"
            className="btn primary"
            style={{ fontSize: 12 }}
            disabled={!dirty || !draft.trim() || updateMut.isPending}
            onClick={() => updateMut.mutate(draft.trim())}
          >
            {updateMut.isPending ? "Saving…" : "Save"}
          </button>
          {saved && (
            <button type="button" className="btn" style={{ fontSize: 12 }} onClick={() => { setDraft(""); updateMut.mutate(""); }}>
              Remove
            </button>
          )}
          {updateMut.isSuccess && !dirty && <span style={{ fontSize: 11, color: "var(--accent)" }}>Saved.</span>}
        </div>
      </div>
    </>
  );
}

export function Settings() {
  return (
    <ErrorBoundary FallbackComponent={SettingsErrorFallback}>
      <SettingsInner />
    </ErrorBoundary>
  );
}
